// @flow
import * as React from 'react';
import classNames from 'classnames/bind';
import { useQuery } from '@apollo/client';
import { pathOr } from 'ramda';

import { LightboxContext } from '../../basic-components/lightbox/context';
import Icon from '../../basic-components/icon';
import AiChatbox from '../ai-chatbox';
import { User as UserQuery } from '../../shared-queries/User.graphql';
import cs from './styles.pcss';

const cx = classNames.bind(cs);

const STORAGE_KEY = 'ai-chat-fab-position';
const FAB_SIZE = 56;
const MOBILE_FAB_SIZE = 48;

// Get saved position from localStorage
const getSavedPosition = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    // Ignore
  }
  return null;
};

// Save position to localStorage
const savePosition = (position) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch (e) {
    // Ignore
  }
};

export default function AiChatFab(): React.Node {
  const { data } = useQuery(UserQuery, { fetchPolicy: 'cache-first' });
  const isWhitelisted = pathOr(false, ['viewer', 'aiChatboxWhiteList'], data);

  const [chatOpened, setChatOpened] = React.useState(false);
  const { lightboxes } = React.useContext(LightboxContext);

  const fabRef = React.useRef(null);
  const dragStateRef = React.useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    startLeft: 0,
    startTop: 0,
    hasMoved: false,
  });
  const justDraggedRef = React.useRef(false);

  const [position, setPosition] = React.useState(() => {
    const saved = getSavedPosition();
    if (saved) {
      return { left: saved.left, top: saved.top, right: null, bottom: null };
    }
    return { left: null, top: null, right: 0, bottom: 130 };
  });

  // Close chat when another lightbox is opened on top
  React.useEffect(() => {
    if (lightboxes.length > 0 && !lightboxes.includes('ai-chatbox')) {
      setChatOpened(false);
    }
  }, [lightboxes]);

  // Constrain position within viewport
  const constrainPosition = (left, top) => {
    const fabSize = window.innerWidth <= 768 ? MOBILE_FAB_SIZE : FAB_SIZE;
    const maxLeft = window.innerWidth - fabSize;
    const maxTop = window.innerHeight - fabSize;

    return {
      left: Math.max(0, Math.min(left, maxLeft)),
      top: Math.max(0, Math.min(top, maxTop)),
    };
  };

  // Drag functionality currently disabled
  // Handle drag start
  // const handleDragStart = (clientX, clientY) => {
  //   if (!fabRef.current) return;

  //   const rect = fabRef.current.getBoundingClientRect();
  //   dragStateRef.current = {
  //     isDragging: true,
  //     startX: clientX,
  //     startY: clientY,
  //     startLeft: rect.left,
  //     startTop: rect.top,
  //     hasMoved: false,
  //   };
  // };

  // Handle drag move
  const handleDragMove = (clientX, clientY) => {
    if (!dragStateRef.current.isDragging) return;

    const deltaX = clientX - dragStateRef.current.startX;
    const deltaY = clientY - dragStateRef.current.startY;

    // Consider it a drag if moved more than 5px
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      dragStateRef.current.hasMoved = true;
    }

    const newLeft = dragStateRef.current.startLeft + deltaX;
    const newTop = dragStateRef.current.startTop + deltaY;

    const constrained = constrainPosition(newLeft, newTop);

    setPosition({
      left: constrained.left,
      top: constrained.top,
      right: null,
      bottom: null,
    });
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (dragStateRef.current.isDragging && dragStateRef.current.hasMoved) {
      // Save position to localStorage
      savePosition({ left: position.left, top: position.top });
      justDraggedRef.current = true;

      // Clear the flag after a short delay to allow click event to check it
      setTimeout(() => {
        justDraggedRef.current = false;
      }, 100);
    }

    dragStateRef.current = {
      isDragging: false,
      startX: 0,
      startY: 0,
      startLeft: 0,
      startTop: 0,
      hasMoved: false,
    };
  };

  // Mouse events - currently disabled
  // const handleMouseDown = (e) => {
  //   e.preventDefault();
  //   handleDragStart(e.clientX, e.clientY);
  // };

  const handleMouseMove = React.useCallback((e) => {
    handleDragMove(e.clientX, e.clientY);
  }, []);

  const handleMouseUp = React.useCallback(() => {
    handleDragEnd();
  }, []);

  // Touch events - currently disabled
  // const handleTouchStart = (e) => {
  //   if (e.touches.length === 1) {
  //     const touch = e.touches[0];
  //     handleDragStart(touch.clientX, touch.clientY);
  //   }
  // };

  const handleTouchMove = React.useCallback((e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      handleDragMove(touch.clientX, touch.clientY);
    }
  }, []);

  const handleTouchEnd = React.useCallback(() => {
    handleDragEnd();
  }, []);

  // Add/remove global event listeners
  React.useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const handleClick = () => {
    // Only open chat if it wasn't a drag
    if (!justDraggedRef.current) {
      setChatOpened(true);
    }
  };

  const handleCloseChat = () => {
    setChatOpened(false);
  };

  const hasOtherLightbox =
    lightboxes.length > 0 && !lightboxes.includes('ai-chatbox');

  const style = {
    ...(position.left !== null && { left: `${position.left}px` }),
    ...(position.top !== null && { top: `${position.top}px` }),
    ...(position.right !== null && { right: `${position.right}px` }),
    ...(position.bottom !== null && { bottom: `${position.bottom}px` }),
    ...(hasOtherLightbox && { zIndex: 99 }),
  };

  if (!isWhitelisted) return null;

  return (
    <>
      {/* Floating Action Button */}
      <button
        ref={fabRef}
        type="button"
        className={cx('fab', {
          hidden: chatOpened,
          dragging: dragStateRef.current.isDragging,
        })}
        style={style}
        // onMouseDown={handleMouseDown}
        // onTouchStart={handleTouchStart}
        onClick={handleClick}
        aria-label="Open AI Chat"
        title="eBay.ai"
      >
        <Icon name="ebayAi" className={cx('fab-icon')} />
      </button>

      {/* Chat Lightbox */}
      <AiChatbox opened={chatOpened} onClose={handleCloseChat} />
    </>
  );
}
